import { Flex, Text, ScrollArea, Grid, Box } from '@radix-ui/themes';
import { ReactNode } from 'react';
import {
    useForm,
    FormProvider,
    DefaultValues,
    FieldValues,
} from 'react-hook-form';

import { FieldRenderer } from './FieldRenderer';
import { FormConfig } from '../types';

interface FormRendererProps<TFormValues extends FieldValues> {
    config: FormConfig<TFormValues>;
    onSubmit: (data: TFormValues) => Promise<void> | void;
    defaultValues?: DefaultValues<TFormValues>;
    footer?: ReactNode;
}

export const FormRenderer = <TFormValues extends FieldValues>({
    config,
    onSubmit,
    defaultValues,
    footer,
}: FormRendererProps<TFormValues>) => {
    const methods = useForm<TFormValues>({
        defaultValues,
        mode: 'onBlur',
    });

    return (
        <FormProvider {...methods}>
            <Flex
                asChild
                direction='column'
                flexGrow='1'
                style={{ minHeight: 0 }}
            >
                <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                    {config.description && (
                        <Flex direction='column' gap='1' mb='2' flexShrink='0'>
                            <Text size='2' color='gray'>
                                {config.description}
                            </Text>
                        </Flex>
                    )}

                    <ScrollArea
                        type='auto'
                        scrollbars='vertical'
                        style={{ flex: 1, outline: 'none' }}
                    >
                        <Grid
                            columns={{ initial: '1', sm: '2' }}
                            gapX='5'
                            gapY='4'
                            pl='2'
                            pt='2'
                            pr='4'
                            pb='2'
                        >
                            {config.fields.map((field) => (
                                <Box
                                    key={field.name}
                                    style={{
                                        gridColumn: [
                                            'array',
                                            'textarea',
                                            'tags',
                                        ].includes(field.type)
                                            ? '1 / -1'
                                            : 'auto',
                                    }}
                                >
                                    <FieldRenderer config={field} />
                                </Box>
                            ))}
                        </Grid>
                    </ScrollArea>

                    {footer && (
                        <Flex
                            pt='4'
                            mt='2'
                            gap='3'
                            justify='end'
                            flexShrink='0'
                            style={{
                                borderTop: '1px solid var(--gray-a4)',
                                backgroundColor: 'var(--color-panel-solid)',
                                zIndex: 10,
                            }}
                        >
                            {footer}
                        </Flex>
                    )}
                </form>
            </Flex>
        </FormProvider>
    );
};
